import { useEffect, useState } from 'react';
import {
  Box, Typography, Button, TextField, Alert, CircularProgress, Chip,
  Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';

// LIVE STOPWATCH DISPLAY — browser-acceptance follow-up. VISUAL ONLY.
// The interval below exists purely to trigger a re-render every
// LIVE_TICK_MS while RUNNING; the NUMBER it displays is recomputed from
// performance.now() - stopwatchStartedAt on every single tick, never
// incremented/counted by the interval itself. The canonical captured
// result (handleStopwatchStop, owned by each caller) independently
// computes its own performance.now() reading at the moment STOP is
// clicked and never reads this component's live-display state at all —
// so STOP accuracy depends only on that one fresh reading, never on
// setInterval's own timing/drift or how many ticks happened to fire.
const LIVE_TICK_MS = 100;

function liveElapsedFrom(startedAt) {
  return Math.round(((performance.now() - startedAt) / 1000) * 100) / 100;
}

// Testing Occurrence / Attempt Protocol (059) — the plain "N of M valid
// attempts" line, rendered whenever occurrence state is actually known.
// Absent (renders nothing) for an athlete's very first attempt of a
// station visit, before any RPC response has established the real
// occurrence — never a guessed/provisional number.
function OccurrenceStatusLine({ occurrence }) {
  if (!occurrence) return null;
  return (
    <Typography sx={{ color: '#9eb8d0', fontSize: '0.85rem', fontWeight: 700, mt: 1 }}>
      {occurrence.validAttemptCount} of {occurrence.plannedMaxAttempts} valid attempts
    </Typography>
  );
}

// Ordinary coaching discretion (059) — a plain +/-1 stepper, no dialog,
// no reason required. Team-only: only ever rendered when the caller
// supplies onChangeMax at all (Personal Testing never does). Disabled
// downward once lowering would drop below the current valid count —
// the server enforces this too, but the button reflects it up front
// rather than inviting a guaranteed-to-fail click.
function ChangeMaxControl({ occurrence, onChangeMax }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const adjust = async (delta) => {
    setSaving(true);
    setError(null);
    try {
      await onChangeMax(occurrence.occurrenceId, occurrence.plannedMaxAttempts + delta);
    } catch (e) {
      setError(e.message || 'Could not change the planned maximum.');
    } finally {
      setSaving(false);
    }
  };

  const canLower = occurrence.plannedMaxAttempts - 1 >= occurrence.validAttemptCount;

  return (
    <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
      <Typography sx={{ color: '#6a8aa8', fontSize: '0.75rem', fontWeight: 700 }}>PLANNED MAX</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button size="small" onClick={() => adjust(-1)} disabled={saving || !canLower} sx={{ minWidth: 32, color: '#6a8aa8' }}>−</Button>
        <Typography sx={{ fontWeight: 800, minWidth: 24, textAlign: 'center' }}>{occurrence.plannedMaxAttempts}</Typography>
        <Button size="small" onClick={() => adjust(1)} disabled={saving} sx={{ minWidth: 32, color: '#6a8aa8' }}>+</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mt: 0.5, fontSize: '0.75rem' }}>{error}</Alert>}
    </Box>
  );
}

// End Test Early (059) — a lightweight inline confirm, never a modal.
// `reasonOptions` present -> Team context, a reason selection is
// required before confirming; `reasonOptions` absent/null -> Personal
// context, a guardian/athlete's own "I'm done" needs no structured
// reason at all.
function EndTestControl({ occurrenceId, reasonOptions, onEndTest }) {
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  if (!confirming) {
    return (
      <Button size="small" onClick={() => setConfirming(true)} sx={{ textTransform: 'none', color: '#f5a623', fontWeight: 700 }}>
        End Test
      </Button>
    );
  }

  const handleConfirm = async () => {
    setSaving(true);
    setError(null);
    try {
      await onEndTest(occurrenceId, { reasonCode: reasonOptions ? reason : null });
      setConfirming(false);
      setReason('');
    } catch (e) {
      setError(e.message || 'Could not end this test.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: 'inline-flex', flexDirection: 'column', gap: 1, alignItems: 'center', mt: 1 }}>
      {reasonOptions && (
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="end-test-reason-label">Reason</InputLabel>
          <Select labelId="end-test-reason-label" label="Reason" value={reason} onChange={e => setReason(e.target.value)}>
            {reasonOptions.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>
      )}
      {error && <Alert severity="error" sx={{ maxWidth: 300 }}>{error}</Alert>}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button size="small" onClick={() => { setConfirming(false); setReason(''); }} disabled={saving} sx={{ textTransform: 'none', color: '#6a8aa8' }}>
          Cancel
        </Button>
        <Button
          size="small" variant="contained" color="warning" onClick={handleConfirm}
          disabled={saving || (Boolean(reasonOptions) && !reason)}
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          {saving ? <CircularProgress size={16} sx={{ color: '#050c16' }} /> : 'Confirm End Test'}
        </Button>
      </Box>
    </Box>
  );
}

// Reopen a Completed Test (059) — Team-only, exceptional, always
// requires a reason. new max defaults to current + 1, adjustable via
// the same lightweight +/-1 stepper style as ChangeMaxControl.
function ReopenControl({ occurrenceId, currentMax, reasonOptions, onReopen }) {
  const [expanded, setExpanded] = useState(false);
  const [newMax, setNewMax] = useState(currentMax + 1);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  if (!expanded) {
    return (
      <Button size="small" variant="outlined" onClick={() => setExpanded(true)} sx={{ textTransform: 'none', fontWeight: 700 }}>
        Reopen Test
      </Button>
    );
  }

  const handleConfirm = async () => {
    setSaving(true);
    setError(null);
    try {
      await onReopen(occurrenceId, newMax, { reasonCode: reason, note: null });
      setExpanded(false);
      setReason('');
    } catch (e) {
      setError(e.message || 'Could not reopen this test.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: 'inline-flex', flexDirection: 'column', gap: 1, alignItems: 'center', mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ color: '#6a8aa8', fontSize: '0.75rem', fontWeight: 700 }}>NEW MAX</Typography>
        <Button size="small" onClick={() => setNewMax(m => Math.max(currentMax + 1, m - 1))} sx={{ minWidth: 32, color: '#6a8aa8' }}>−</Button>
        <Typography sx={{ fontWeight: 800, minWidth: 24, textAlign: 'center' }}>{newMax}</Typography>
        <Button size="small" onClick={() => setNewMax(m => m + 1)} sx={{ minWidth: 32, color: '#6a8aa8' }}>+</Button>
      </Box>
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <InputLabel id="reopen-reason-label">Reason</InputLabel>
        <Select labelId="reopen-reason-label" label="Reason" value={reason} onChange={e => setReason(e.target.value)}>
          {reasonOptions.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </Select>
      </FormControl>
      {error && <Alert severity="error" sx={{ maxWidth: 300 }}>{error}</Alert>}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button size="small" onClick={() => setExpanded(false)} disabled={saving} sx={{ textTransform: 'none', color: '#6a8aa8' }}>
          Cancel
        </Button>
        <Button size="small" variant="contained" color="primary" onClick={handleConfirm} disabled={saving || !reason} sx={{ textTransform: 'none', fontWeight: 700 }}>
          {saving ? <CircularProgress size={16} sx={{ color: '#050c16' }} /> : 'Confirm Reopen'}
        </Button>
      </Box>
    </Box>
  );
}

// Phase — PERSONAL TESTING STATION. Extracted, byte-for-byte-faithful
// pull of TeamTestingStation.js's own 'armed'/'feedback' JSX (C.7a/
// C.7a.1) into a shared, presentational, props-in/JSX-out component —
// no state of its own, no Supabase calls, no knowledge of Team vs
// Personal context. This is the ONE piece TeamTestingStation and
// PersonalTestingStation both render once a subject (a roster row, or
// an already-known Athlete Profile athlete) is known and a measurement
// method has been chosen — manual entry OR the FFN Stopwatch state
// machine, then the PB-feedback screen.
//
// Deliberately NOT shared: test-type Tabs, the measurement-method gate,
// and roster lookup/confirmation — those stay in each caller (small,
// controlled, and each caller's own persistence-across-attempts
// requirements differ enough, per the Personal Testing Station
// architecture audit, that duplicating this thin layer is safer than
// forcing it through one more level of props).
//
// `subject` is a plain { jerseyNumber, firstName, lastName } shape —
// never a roster row directly — so this component has no dependency on
// team_athletes at all; PersonalTestingStation passes jerseyNumber: null.
//
// Testing Occurrence / Attempt Protocol (059) — `occurrence` is the
// caller's own cached knowledge of THIS subject+test's occurrence state
// ({ occurrenceId, status, validAttemptCount, plannedMaxAttempts }),
// established from a prior recordAttempt() response — `null`/undefined
// for a subject+test never yet attempted this visit, in which case this
// component renders exactly as it always has (no occurrence UI at all).
// Once known and `status === 'completed'`, the normal capture UI
// (manual entry / FFN Stopwatch) is replaced entirely by a Test
// Complete state — the server (059) is the actual authority regardless;
// this is purely a courtesy so a coach/guardian is never surprised by a
// rejected Record call. `onChangeMax`/`onReopen` are Team-only and
// simply omitted by PersonalTestingStation; `onEndTest` is shared by
// both, with `endTestReasonOptions` present only for Team.
export default function TestAttemptCapture({
  phase, // 'armed' | 'feedback'
  subject,
  previousPb,
  unitLabel,
  isAppStopwatch,
  stopwatchPhase,
  stopwatchStartedAt,
  candidateResult,
  resultValue,
  onResultChange,
  error,
  saving,
  onStopwatchStart,
  onStopwatchStop,
  onStopwatchRetry,
  onStopwatchRecord,
  onManualRecord,
  onCancel,
  feedback,
  nextActionLabel,
  onNext,
  occurrence,
  onEndTest,
  endTestReasonOptions,
  onChangeMax,
  onReopen,
  reopenReasonOptions,
  // Testing Occurrence / Attempt Protocol (059) — feedback-phase only.
  // `nextActionLabel`/`onNext` means two DIFFERENT things depending on
  // caller: Personal's "Test Again" is a literal capture-continuation
  // for THIS SAME occurrence (contradictory once it's completed — a
  // guardian must never be invited to add a 3rd attempt after ending
  // the test at 2 of 3); Team's "Next Athlete" is a navigation action
  // (clear the armed athlete, return to roster lookup) that stays
  // perfectly valid regardless of whether the JUST-tested athlete's own
  // occurrence is complete — a coach still needs a way off this screen
  // to test the next kid. PersonalTestingStation.js passes this as
  // `true`; TeamTestingStation.js omits it (defaults false/undefined),
  // preserving "Next Athlete" exactly as it has always behaved.
  hideNextWhenCompleted,
}) {
  const subjectName = `${subject?.jerseyNumber !== null && subject?.jerseyNumber !== undefined ? `#${subject.jerseyNumber} ` : ''}${subject?.firstName || ''} ${subject?.lastName || ''}`;

  // Live elapsed display — see the module-level comment above. Ticks
  // ONLY while stopwatchPhase === 'running' AND a start time exists;
  // resets to null the instant that's no longer true (STOP, RETRY, a
  // fresh START, Cancel, or unmount) — React's own effect-cleanup
  // handles all of those uniformly, no separate cancel-path needed.
  const [liveElapsed, setLiveElapsed] = useState(null);
  useEffect(() => {
    if (stopwatchPhase !== 'running' || stopwatchStartedAt == null) {
      setLiveElapsed(null);
      return undefined;
    }
    setLiveElapsed(liveElapsedFrom(stopwatchStartedAt)); // advance immediately, not after the first tick delay
    const id = setInterval(() => setLiveElapsed(liveElapsedFrom(stopwatchStartedAt)), LIVE_TICK_MS);
    return () => clearInterval(id);
  }, [stopwatchPhase, stopwatchStartedAt]);

  if (phase === 'armed') {
    // Testing Occurrence / Attempt Protocol (059) — a completed
    // occurrence replaces the entire capture surface with a Test
    // Complete state. Cancel remains available (back to lookup, nothing
    // persisted) even here.
    if (occurrence?.status === 'completed') {
      return (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Box sx={{ border: '3px solid #6a8aa8', borderRadius: 3, p: 3, mb: 3, backgroundColor: 'rgba(106,138,160,0.08)' }}>
            <Chip label="TEST COMPLETE" sx={{ bgcolor: '#1a2e44', color: '#9eb8d0', fontWeight: 900, fontSize: '0.9rem', mb: 1.5, px: 1 }} />
            <Typography sx={{ fontWeight: 900, fontSize: '2rem' }}>{subjectName}</Typography>
            <OccurrenceStatusLine occurrence={occurrence} />
          </Box>
          {onReopen && (
            <ReopenControl
              occurrenceId={occurrence.occurrenceId}
              currentMax={occurrence.plannedMaxAttempts}
              reasonOptions={reopenReasonOptions}
              onReopen={onReopen}
            />
          )}
          <Box sx={{ mt: 3 }}>
            <Button onClick={onCancel} sx={{ textTransform: 'none', color: '#6a8aa8' }}>
              Back
            </Button>
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        {/* Deliberately not color-only: a thick border + explicit
            "ARMED" label + large type all together, so the state
            reads from several feet away. */}
        <Box sx={{ border: '3px solid #2ea84b', borderRadius: 3, p: 3, mb: 3, backgroundColor: 'rgba(46,168,75,0.08)' }}>
          <Chip label="ARMED" sx={{ bgcolor: '#2ea84b', color: '#050c16', fontWeight: 900, fontSize: '0.9rem', mb: 1.5, px: 1 }} />
          <Typography sx={{ fontWeight: 900, fontSize: '2rem' }}>{subjectName}</Typography>
          <Typography sx={{ color: '#2ea84b', fontWeight: 800, fontSize: '1.1rem', mt: 0.5 }}>READY TO GO</Typography>
          {previousPb !== undefined && (
            <Typography sx={{ color: '#9eb8d0', mt: 1.5 }}>
              {previousPb ? `Previous PB: ${previousPb.value} ${unitLabel}` : 'No previous result on file'}
            </Typography>
          )}
          <OccurrenceStatusLine occurrence={occurrence} />
        </Box>

        {isAppStopwatch ? (
          <Box>
            {stopwatchPhase === 'ready' && (
              <Button
                onClick={onStopwatchStart} variant="contained" color="primary" size="large"
                sx={{ textTransform: 'none', fontWeight: 900, px: 6, py: 3, fontSize: '1.4rem' }}
              >
                START
              </Button>
            )}
            {stopwatchPhase === 'running' && (
              <Box>
                <Typography sx={{ fontWeight: 900, fontSize: '1.3rem', color: '#f5a623', mb: 1 }}>RUNNING…</Typography>
                {/* VISUAL ONLY — see the module-level comment. Never the
                    source of the captured result. */}
                <Typography sx={{ fontWeight: 900, fontSize: '2.2rem', mb: 2 }}>
                  {(liveElapsed !== null ? liveElapsed : 0).toFixed(2)} {unitLabel}
                </Typography>
                <Button
                  onClick={onStopwatchStop} variant="contained" color="secondary" size="large"
                  sx={{ textTransform: 'none', fontWeight: 900, px: 6, py: 3, fontSize: '1.4rem' }}
                >
                  STOP
                </Button>
              </Box>
            )}
            {stopwatchPhase === 'captured' && (
              <Box>
                <Typography sx={{ color: '#6a8aa8', fontSize: '0.85rem', mb: 0.5 }}>Elapsed</Typography>
                <Typography sx={{ fontWeight: 900, fontSize: '2.4rem', mb: 2 }}>{candidateResult} {unitLabel}</Typography>
                {error && <Alert severity="error" sx={{ mb: 2, maxWidth: 400, mx: 'auto' }}>{error}</Alert>}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Button onClick={onStopwatchRetry} disabled={saving} variant="outlined" color="secondary" size="large" sx={{ textTransform: 'none', fontWeight: 800, px: 4 }}>
                    Retry
                  </Button>
                  <Button onClick={onStopwatchRecord} disabled={saving} variant="contained" color="primary" size="large" sx={{ textTransform: 'none', fontWeight: 800, px: 5 }}>
                    {saving ? <CircularProgress size={22} sx={{ color: '#050c16' }} /> : 'Record'}
                  </Button>
                </Box>
              </Box>
            )}
            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center', alignItems: 'flex-start' }}>
              <Button onClick={onCancel} disabled={saving} sx={{ textTransform: 'none', color: '#6a8aa8' }}>
                Cancel
              </Button>
              {occurrence && onChangeMax && <ChangeMaxControl occurrence={occurrence} onChangeMax={onChangeMax} />}
              {occurrence && onEndTest && (
                <EndTestControl occurrenceId={occurrence.occurrenceId} reasonOptions={endTestReasonOptions} onEndTest={onEndTest} />
              )}
            </Box>
          </Box>
        ) : (
          <Box>
            <TextField
              label={`Result (${unitLabel})`} type="number" size="medium" autoFocus
              value={resultValue}
              onChange={e => onResultChange(e.target.value)}
              inputProps={{ step: 0.01, min: 0, inputMode: 'decimal', style: { fontSize: '1.6rem', fontWeight: 800, textAlign: 'center' } }}
              sx={{ width: 220, mb: 2 }}
            />

            {error && <Alert severity="error" sx={{ mb: 2, maxWidth: 400, mx: 'auto' }}>{error}</Alert>}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button onClick={onCancel} disabled={saving} variant="outlined" color="secondary" size="large" sx={{ textTransform: 'none', fontWeight: 800, px: 4 }}>
                Cancel
              </Button>
              <Button onClick={onManualRecord} disabled={saving} variant="contained" color="primary" size="large" sx={{ textTransform: 'none', fontWeight: 800, px: 5 }}>
                {saving ? <CircularProgress size={22} sx={{ color: '#050c16' }} /> : 'Record'}
              </Button>
            </Box>
            <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'center', alignItems: 'flex-start' }}>
              {occurrence && onChangeMax && <ChangeMaxControl occurrence={occurrence} onChangeMax={onChangeMax} />}
              {occurrence && onEndTest && (
                <EndTestControl occurrenceId={occurrence.occurrenceId} reasonOptions={endTestReasonOptions} onEndTest={onEndTest} />
              )}
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  // phase === 'feedback'
  return (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <Typography sx={{ color: '#6a8aa8', fontSize: '1rem', mb: 1 }}>{subjectName}</Typography>
      <Typography sx={{ fontWeight: 900, fontSize: '2.4rem', mb: 1 }}>
        {feedback.value} {feedback.unit}
      </Typography>
      {feedback.isNewPb ? (
        <Chip label="NEW PB" sx={{ bgcolor: '#2ea84b', color: '#050c16', fontWeight: 900, fontSize: '1rem', px: 2, py: 2.5 }} />
      ) : (
        <Typography sx={{ color: '#9eb8d0' }}>
          Previous PB: {previousPb?.value} {feedback.unit}
        </Typography>
      )}
      <OccurrenceStatusLine occurrence={occurrence} />
      {occurrence?.status === 'completed' && (
        <Typography sx={{ color: '#9eb8d0', fontWeight: 800, mt: 1 }}>Test Complete</Typography>
      )}
      <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center', alignItems: 'flex-start' }}>
        {/* BUGFIX — this button used to render unconditionally, so a
            completed Personal occurrence still offered "Test Again"
            immediately after End Test, contradicting the very state
            just shown above ("Test Complete"). Team's "Next Athlete" is
            a different, always-valid action (see the prop comment
            above) and is unaffected — hideNextWhenCompleted is never
            passed there. */}
        {!(hideNextWhenCompleted && occurrence?.status === 'completed') && (
          <Button onClick={onNext} variant="contained" color="primary" size="large" sx={{ textTransform: 'none', fontWeight: 800, px: 5 }}>
            {nextActionLabel}
          </Button>
        )}
        {occurrence?.status === 'active' && onEndTest && (
          <EndTestControl occurrenceId={occurrence.occurrenceId} reasonOptions={endTestReasonOptions} onEndTest={onEndTest} />
        )}
      </Box>
    </Box>
  );
}
